import { PasswordInput } from "@/components/ui/PasswordInput";
import React, { useState } from 'react';
import { VStack, HStack, Text, Box, Input, Button } from '@chakra-ui/react';
import { Copy, User } from 'lucide-react';
import Drawer, { DrawerHeader, DrawerTitle, DrawerBody, DrawerFooter, DrawerCloseTrigger } from '@/components/ui/Drawer';
import withHashDrawer from '@/components/ui/withHashDrawer';
import { useCloneProfileMutation, useGetUserByIdQuery } from '../api';

interface CloneProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityId?: number;
  onSuccess?: () => void;
}

const CloneProfileModal: React.FC<CloneProfileModalProps> = ({
  isOpen,
  onClose,
  entityId,
  onSuccess,
}) => {
  const [newEmail, setNewEmail] = useState('');
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [password, setPassword] = useState('password');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { data: user } = useGetUserByIdQuery(entityId!, {
    skip: !entityId,
  });

  // Clone options state
  const [cloneOptions, setCloneOptions] = useState({
    webPresence: true, // Maps to profile_achievements_to_clone
    locations: true,
    phones: true,
    conditions: true,
    conditionResources: true,
    conditionPrescriptions: true,
    conditionVideos: true, // Maps to condition_recommendations_to_clone
    assistants: true,
  });

  const [cloneProfile, { isLoading: isCloning }] = useCloneProfileMutation();

  const handleCloneOptionChange = (option: keyof typeof cloneOptions) => {
    setCloneOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  const handleSubmit = async () => {
    if (!user || !newEmail || !newFirstName || !newLastName) {
      setError('Please fill in all required fields');
      return;
    }

    if (!user.organization?.id || !user.profile?.id) {
      setError('User must have an organization and profile to clone');
      return;
    }

    setError('');

    try {
      await cloneProfile({
        organization_id: user.organization.id,
        profile_ids: [user.profile.id],
        email: newEmail.trim(),
        first_name: newFirstName.trim(),
        last_name: newLastName.trim(),
        password: password || 'password',
        // Map frontend options to API fields
        profile_achievements_to_clone: cloneOptions.webPresence,
        profile_locations_to_clone: cloneOptions.locations,
        profile_phones_to_clone: cloneOptions.phones,
        profile_references_to_clone: false, // Not exposed in UI
        profile_testimonials_to_clone: false, // Not exposed in UI
        conditions_to_clone: cloneOptions.conditions,
        condition_resources_to_clone: cloneOptions.conditionResources,
        condition_prescriptions_to_clone: cloneOptions.conditionPrescriptions,
        condition_recommendations_to_clone: cloneOptions.conditionVideos,
        assistants_to_clone: cloneOptions.assistants,
      }).unwrap();
      
      onSuccess?.();
      onClose();
      resetForm();
    } catch (err: any) {
      setError(err.data?.message || err.message || 'Failed to clone profile');
    }
  };

  const resetForm = () => {
    setNewEmail('');
    setNewFirstName('');
    setNewLastName('');
    setPassword('password');
    setError('');
    setCloneOptions({
      webPresence: true,
      locations: true,
      phones: true,
      conditions: true,
      conditionResources: true,
      conditionPrescriptions: true,
      conditionVideos: true,
      assistants: true,
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} size="md">
      <DrawerHeader>
        <DrawerTitle color="brand.700" _dark={{ color: "brand.300" }}>
          <HStack>
            <Copy size={20} />
            <Text>Clone Profile</Text>
          </HStack>
        </DrawerTitle>
        <DrawerCloseTrigger />
      </DrawerHeader>
      
      <DrawerBody>
        <VStack gap={6} align="stretch">
          {/* Original User Info */}
          <Box 
            p={4} 
            borderWidth={1} 
            borderRadius="md" 
            borderColor="gray.200" 
            bg="gray.50"
            _dark={{ 
              borderColor: "gray.600", 
              bg: "gray.800" 
            }}
          >
            <VStack align="start" gap={2}>
              <HStack>
                <User size={16} />
                <Text fontSize="sm" fontWeight="medium" color="gray.600" _dark={{ color: "gray.400" }}>
                  Cloning from:
                </Text>
              </HStack>
              <Text fontWeight="semibold">{user?.email}</Text>
              <Text fontSize="sm" color="gray.600" _dark={{ color: "gray.400" }}>
                {user?.first_name} {user?.last_name}
              </Text>
              <Text fontSize="sm" color="gray.600" _dark={{ color: "gray.400" }}>
                Roles: {user?.roles?.join(', ') || 'No roles'}
              </Text>
            </VStack>
          </Box>

          {/* New User Form */}
          <VStack gap={4} align="stretch">
            <div>
              <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={1} _dark={{ color: "gray.400" }}>
                New Email <Text as="span" color="red.500">*</Text>
              </Text>
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Enter email for cloned profile"
              />
            </div>

            <div>
              <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={1} _dark={{ color: "gray.400" }}>
                First Name <Text as="span" color="red.500">*</Text>
              </Text>
              <Input
                value={newFirstName}
                onChange={(e) => setNewFirstName(e.target.value)}
                placeholder="Enter first name"
              />
            </div>

            <div>
              <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={1} _dark={{ color: "gray.400" }}>
                Last Name <Text as="span" color="red.500">*</Text>
              </Text>
              <Input
                value={newLastName}
                onChange={(e) => setNewLastName(e.target.value)}
                placeholder="Enter last name"
              />
            </div>

            <div>
              <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={1} _dark={{ color: "gray.400" }}>
                Password <Text fontSize="xs" color="gray.500">(Not required - default is "password")</Text>
              </Text>
              <PasswordInput
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
              />
              {error && (
                <Text color="red.500" fontSize="sm" mt={1}>
                  {error}
                </Text>
              )}
            </div>
          </VStack>

          {/* Clone Options */}
          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={3} color="gray.600" _dark={{ color: "gray.400" }}>
              Clone Options
            </Text>
            <VStack gap={2} align="stretch">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={cloneOptions.webPresence}
                  onChange={() => handleCloneOptionChange('webPresence')}
                />
                <Text fontSize="sm">Clone Web Presence?</Text>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={cloneOptions.locations}
                  onChange={() => handleCloneOptionChange('locations')}
                />
                <Text fontSize="sm">Clone Profile Locations?</Text>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={cloneOptions.phones}
                  onChange={() => handleCloneOptionChange('phones')}
                />
                <Text fontSize="sm">Clone Profile Phones?</Text>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={cloneOptions.conditions}
                  onChange={() => handleCloneOptionChange('conditions')}
                />
                <Text fontSize="sm">Clone Conditions?</Text>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={cloneOptions.conditionResources}
                  onChange={() => handleCloneOptionChange('conditionResources')}
                />
                <Text fontSize="sm">Clone Condition Resources?</Text>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={cloneOptions.conditionPrescriptions}
                  onChange={() => handleCloneOptionChange('conditionPrescriptions')}
                />
                <Text fontSize="sm">Clone Condition Prescriptions?</Text>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={cloneOptions.conditionVideos}
                  onChange={() => handleCloneOptionChange('conditionVideos')}
                />
                <Text fontSize="sm">Clone Condition Videos/Recommendations?</Text>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={cloneOptions.assistants}
                  onChange={() => handleCloneOptionChange('assistants')}
                />
                <Text fontSize="sm">Clone Assistants?</Text>
              </label>
            </VStack>
          </Box>

          <Box 
            p={3} 
            borderWidth={1} 
            borderRadius="md" 
            borderColor="blue.200" 
            bg="blue.50"
            _dark={{ 
              borderColor: "blue.600", 
              bg: "blue.900" 
            }}
          >
            <Text fontSize="sm" color="blue.700" _dark={{ color: "blue.200" }}>
              This will create a new user with the same profile settings, and organization as the original user. Selected options will be cloned; unchecked items will not be copied to the new profile.
            </Text>
          </Box>
        </VStack>
      </DrawerBody>
      
      <DrawerFooter>
        <HStack gap={3}>
          <Button variant="outline" onClick={handleClose} disabled={isCloning}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            loading={isCloning}
            disabled={isCloning || !newEmail || !newFirstName || !newLastName}
            colorPalette="blue"
          >
            Clone Profile
          </Button>
        </HStack>
      </DrawerFooter>
    </Drawer>
  );
};

export default withHashDrawer(CloneProfileModal, {
  action: 'clone-profile',
});
